// src/services/ChildService.ts
import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  addDoc,
  deleteDoc,
  query,
  where,
  Timestamp,
} from "firebase/firestore";
import { Child, ClientFile } from "@visitproof/types";
import { db } from "../firebase";
import { clean } from "../utils/utils";

type File = ClientFile;

const childCollectionRef = collection(db, "children");

export const ChildService = {
  async createChild(
    childData: Omit<Child, "id" | "createdAt" | "updatedAt">,
  ): Promise<string> {
    const newChild: Omit<Child, "id"> = {
      ...childData,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };

    const childRef = await addDoc(childCollectionRef, clean(newChild));
    return childRef.id;
  },

  async getChild(childId: string): Promise<Child | null> {
    const childDoc = await getDoc(doc(childCollectionRef, childId));
    if (childDoc.exists()) {
      return { id: childDoc.id, ...childDoc.data() } as Child;
    }
    return null;
  },

  async getAllChildren(accountId: string): Promise<Child[]> {
    const childSnapshot = await getDocs(
      query(childCollectionRef, where("accountId", "==", accountId)),
    );
    return childSnapshot.docs.map(
      (doc) => ({ id: doc.id, ...doc.data() }) as Child,
    );
  },

  async updateChild(
    childId: string,
    updatedData: Partial<Omit<Child, "id" | "createdAt">>,
  ): Promise<void> {
    const childDocRef = doc(childCollectionRef, childId);
    await updateDoc(childDocRef, clean({
      ...updatedData,
      updatedAt: Timestamp.now(),
    }));
  },

  async deleteChild(childId: string): Promise<void> {
    const childDocRef = doc(childCollectionRef, childId);
    await deleteDoc(childDocRef);
  },

  async getChildFiles(childId: string): Promise<File[]> {
    const childFilesSnapshot = await getDocs(
      collection(db, "children", childId, "files"),
    );
    if (childFilesSnapshot.empty) {
      return [];
    }
    return childFilesSnapshot.docs.map(
      (doc) => ({ id: doc.id, ...doc.data() }) as File,
    );
  },

  async addFileToChild(
    childId: string,
    fileData: Omit<File, "id" | "createdAt" | "updatedAt">,
  ): Promise<string> {
    const newFile: Omit<File, "id"> = {
      ...fileData,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };
    const fileRef = await addDoc(
      collection(db, "children", childId, "files"),
      newFile,
    );
    return fileRef.id;
  },

  async deleteFileFromChild(childId: string, fileId: string): Promise<void> {
    const fileRef = doc(collection(db, "children", childId, "files"), fileId);
    await deleteDoc(fileRef);
  },
};
